import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from './api';
import {
  Box, Typography, TextField, Button, List,
  Accordion, AccordionSummary, AccordionDetails,
  Grid, Card, CardContent, Alert, LinearProgress,
  FormControl, InputLabel, Select, MenuItem,
  ToggleButton, ToggleButtonGroup, useTheme, Chip, IconButton, Tooltip
} from '@mui/material';
import { formatJobEnteredAt, formatJobScheduledFor } from './utils/jobSchedule';
import {
  ExpandMore, AttachFile, Description, Person, LocationOn, Phone,
  Email, Schedule, CalendarToday, CheckCircle, Pending, PlayArrow, Cancel, Add as AddIcon,
  FirstPage as FirstPageIcon, LastPage as LastPageIcon, NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon, ViewColumn as ViewColumnIcon, Search as SearchIcon
} from '@mui/icons-material';

export default function Collections() {
  const theme = useTheme();
  const [collections, setCollections] = useState([]);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all'); // all, completed, active
  const [showColumns, setShowColumns] = useState(false);

  const fetchCollections = async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}collections?page=${page}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        // Handle both paginated (object) and non-paginated (array - fallback) responses
        if (Array.isArray(data)) {
          setCollections(data);
          setTotalPages(1);
        } else if (data.data) {
          setCollections(data.data);
          setTotalPages(data.last_page);
          setCurrentPage(data.current_page);
        }
      } else {
        if (res.status === 401) {
          setError('Unauthorized. Please login.');
        }
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
      setError('Failed to fetch collections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections(currentPage);
  }, [currentPage]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}collections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name })
      });
      if (res.ok) {
        setSuccess('Collection created!');
        setName('');
        fetchCollections(currentPage);
      } else {
        setError('Error creating collection');
      }
    } catch (error) {
      setError('Error creating collection');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle fontSize="small" />;
      case 'in_progress': return <PlayArrow fontSize="small" />;
      case 'pending': return <Pending fontSize="small" />;
      case 'cancelled': return <Cancel fontSize="small" />;
      default: return <Pending fontSize="small" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getFilteredCollections = () => {
    switch (filter) {
      case 'active':
        return collections.filter(collection => collection.status !== 'completed');
      case 'completed':
        return collections.filter(collection => collection.status === 'completed');
      default:
        return collections;
    }
  };

  const displayedCollections = getFilteredCollections();

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  const completedCollections = collections.filter(collection => collection.status === 'completed');
  const activeCollections = collections.filter(collection => collection.status !== 'completed');

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 4 }}>
        <LinearProgress sx={{ borderRadius: 2, height: 8 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
            Collections
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Manage your jobs
          </Typography>
        </Box>
      </Box>

      {/* Messages */}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={4}>
        {/* Create Collection Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 'fit-content', position: 'sticky', top: 24, mb: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: '50%',
                  backgroundColor: '#E7F3FF',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#1877F2'
                }}>
                  <AddIcon fontSize="medium" />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  New Job
                </Typography>
              </Box>

              <form onSubmit={handleCreate}>
                <TextField
                  fullWidth
                  label="Name"
                  placeholder="e.g. Acme Corp Pickup"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  sx={{ mb: 3 }}
                  variant="outlined"
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: '#1877F2',
                    boxShadow: 'none',
                    fontWeight: 700,
                    textTransform: 'none',
                    py: 1.5,
                    fontSize: '1rem',
                    '&:hover': {
                      backgroundColor: '#166fe5',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Create Job
                </Button>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Collections List */}
        <Grid item xs={12} md={8}>
          <Card sx={{ minHeight: '600px', boxShadow: 'none', border: 'none', backgroundColor: 'transparent' }}>
            {/* Feed Header - Separated from Card to mimic feed style */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1.5, sm: 0 },
              mb: 2,
              backgroundColor: '#fff',
              p: 2,
              borderRadius: '8px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                  All Jobs
                </Typography>
                <Chip
                  label={displayedCollections.length}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    backgroundColor: '#F0F2F5',
                    color: '#65676B'
                  }}
                />
              </Box>

              <FormControl size="small" sx={{ minWidth: 140, width: { xs: '100%', sm: 'auto' } }}>
                <Select
                  value={filter}
                  onChange={handleFilterChange}
                  displayEmpty
                  variant="standard"
                  disableUnderline
                  sx={{
                    fontWeight: 600,
                    color: '#1877F2',
                    fontSize: '0.95rem',
                    '& .MuiSelect-select': { paddingRight: '24px !important' }
                  }}
                  IconComponent={ExpandMore}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {displayedCollections.length > 0 ? (
                displayedCollections.map((collection) => (
                  <Card
                    key={collection.id}
                    elevation={0}
                    sx={{
                      borderRadius: '8px',
                      border: 'none',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      transition: 'transform 0.2s',
                      backgroundColor: '#fff',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5, fontSize: '1.05rem' }}>
                            {collection.name || 'Unnamed Collection'}
                          </Typography>
                          {collection.client && (
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Person sx={{ fontSize: 16 }} /> {collection.client}
                            </Typography>
                          )}
                        </Box>

                        <Chip
                          label={collection.status?.replace('_', ' ') || 'pending'}
                          size="small"
                          sx={{
                            backgroundColor: collection.status === 'completed' ? '#E6F6EC' : '#EBF5FF',
                            color: collection.status === 'completed' ? '#0F8844' : '#0866FF',
                            fontWeight: 700,
                            borderRadius: '6px',
                            px: 1,
                            height: 28,
                            fontSize: '0.8rem'
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#65676B', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <CalendarToday sx={{ fontSize: 18, color: '#65676B', mt: 0.2 }} />
                          <Box>
                            <Typography component="span" variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                              Entered in system
                            </Typography>
                            {formatJobEnteredAt(collection) || 'Not set'}
                          </Box>
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#65676B', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Schedule sx={{ fontSize: 18, color: '#65676B', mt: 0.2 }} />
                          <Box>
                            <Typography component="span" variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                              Scheduled for work
                            </Typography>
                            {formatJobScheduledFor(collection) || 'Not set'}
                          </Box>
                        </Typography>
                      </Box>

                      {/* Accordion equivalent for Notes */}
                      {(collection.driver_notes || (collection.files && collection.files.length > 0)) && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                          {collection.driver_notes && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary, display: 'block', mb: 0.5, letterSpacing: '0.05em' }}>
                                DRIVER NOTES
                              </Typography>
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary, lineHeight: 1.5 }}>
                                {collection.driver_notes}
                              </Typography>
                            </Box>
                          )}

                          {collection.files && collection.files.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                              {collection.files.map((file, idx) => {
                                const fileName = typeof file === 'string' ? file.split('/').pop() : file.name;
                                return (
                                  <Chip
                                    key={idx}
                                    icon={<AttachFile sx={{ fontSize: 14 }} />}
                                    label={fileName}
                                    size="small"
                                    onClick={() => window.open(typeof file === 'string' ? file : file.url, '_blank')}
                                    sx={{ borderRadius: '6px', height: 24, backgroundColor: '#F0F2F5' }}
                                  />
                                )
                              })}
                            </Box>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Box sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: '#F0F2F5', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <Description sx={{ fontSize: 32, color: '#65676B' }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 1 }}>
                    No jobs found
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Try adjusting your filters or create a new job.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, backgroundColor: '#fff', px: 2, py: 1, borderRadius: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <IconButton onClick={goToPreviousPage} disabled={currentPage === 1} size="small" sx={{ color: '#1877F2' }}>
                    <NavigateBeforeIcon />
                  </IconButton>
                  <Typography variant="body2" sx={{ mx: 1, fontWeight: 700, color: '#1877F2' }}>
                    Page {currentPage} of {totalPages}
                  </Typography>
                  <IconButton onClick={goToNextPage} disabled={currentPage === totalPages} size="small" sx={{ color: '#1877F2' }}>
                    <NavigateNextIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}